<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css/bundle";
import "swiper/css";

import { useContentInfo } from "@/stores/contentInfo";
const contentInfo = useContentInfo();

const modules = [Pagination, Autoplay];

//處理輪播圖秒數用
const modValue = ref<number | undefined>(3000);
const modState = ref(false);

function checkPictureSeconds() {
  if (contentInfo.PictureSeconds !== undefined) {
    modValue.value = contentInfo.PictureSeconds;
    modState.value = true;
  } else {
    // 如果值仍然是 undefined，等待1秒後再次檢查
    setTimeout(checkPictureSeconds, 1000); // 1000毫秒等於1秒
  }
}
onMounted(() => {
  // 開始檢查
  checkPictureSeconds();
});
// 嚴格 lazy：初始只載前 2 張，其餘先掛透明佔位，輪播移動到鄰近（當前±1）才填入真實網址。
// 原生 loading="lazy" 在 Chrome 的水平預載閾值太寬，實測初始仍會下載大半輪播圖。
const TRANSPARENT_PX =
  "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const loadedIdx = ref<number[]>([0, 1]);
function markLoaded(...idxs: number[]) {
  const next = new Set(loadedIdx.value);
  idxs.forEach((i) => next.add(i));
  if (next.size !== loadedIdx.value.length) loadedIdx.value = [...next];
}
function bannerSrc(url: string, idx: number | string) {
  return loadedIdx.value.includes(Number(idx)) ? url : TRANSPARENT_PX;
}
function isBannerLoaded(idx: number | string) {
  return loadedIdx.value.includes(Number(idx));
}
const onSlideChange = (swiper: { realIndex: number }) => {
  const total = Object.keys(
    contentInfo.contentInfo.aIndexBannerPic || {}
  ).length;
  if (!total) return;
  const i = swiper.realIndex;
  markLoaded(i, (i + 1) % total, (i - 1 + total) % total);
};

// 輪播媒體（圖片／影片）載入失敗容錯：重試 2 次（帶 cache-buster 避開快取到的壞回應），
// 仍失敗則隱藏破圖，露出透明容器（顯示頁面背景），避免藍色問號與版面跳動
const BANNER_RETRY_MAX = 2;
function onBannerImgError(e: Event) {
  const img = e.target as HTMLImageElement | HTMLVideoElement;
  const retried = Number(img.dataset.retry || 0);
  if (retried < BANNER_RETRY_MAX) {
    img.dataset.retry = String(retried + 1);
    const base = img.dataset.base || img.src.split("?")[0];
    img.dataset.base = base;
    setTimeout(() => {
      img.src = base + "?r=" + (retried + 1);
    }, 1000 * (retried + 1));
  } else {
    img.style.visibility = "hidden";
  }
}
function onBannerImgLoad(e: Event) {
  (e.target as HTMLImageElement).style.visibility = "visible";
}
</script>
<template>
  <div
    class="w-full px-1"
    v-if="modState"
  >
    <Swiper
      cssMode: true
      :slides-per-view="1"
      :space-between="25"
      :modules="modules"
      loop
      :autoplay="{
        delay: modValue,
        disableOnInteraction: false,
      }"
      :pagination="{
        clickable: false,
        clickableClass: '',
      }"
      :breakpoints="{ 
        768: {
          pagination: {
            clickable: true,
            clickableClass: 'content-swiper-pagination',
          }
        }
      }"
      class="h-full"
      @slideChange="onSlideChange"
    >
      <SwiperSlide
        v-for="(item, idx) in contentInfo.contentInfo.aIndexBannerPic"
        :key="item.nId"
      >
        <!-- 手機版：容器固定 1000/360 長寬比＝後台手機稿件規格（web_banner_upt 首頁輪播
             1000px*360px），同比例才不會被 object-cover 左右裁切；固定比例同時保證媒體
             載入失敗或尺寸異常都不會撐壞版面。
             video 僅作展示：無 controls＋pointer-events-none（點擊穿透到外層 a 連結，
             使用者無法暫停或操作影片），muted+playsinline 是 iOS 自動播放的硬性要求 -->
        <a
          :href="item.sUrl"
          class="block md:hidden w-full aspect-[1000/360] overflow-hidden"
        >
          <video
            v-if="item.sMediaTypePhone === 'video' && isBannerLoaded(idx)"
            :src="item.sImgUrlPhone"
            autoplay
            muted
            loop
            playsinline
            disablepictureinpicture
            class="w-full h-full object-cover pointer-events-none"
            @error="onBannerImgError"
            @loadeddata="onBannerImgLoad"
          />
          <img
            v-else-if="item.sMediaTypePhone !== 'video'"
            :src="bannerSrc(item.sImgUrlPhone, idx)"
            alt=""
            loading="lazy"
            class="w-full h-full object-cover"
            @error="onBannerImgError"
            @load="onBannerImgLoad"
          />
        </a>
        <!-- 桌機版：容器固定 1900/500 長寬比＝後台桌機稿件規格（web_banner_upt 首頁輪播
             1900px*500px）。固定比例防嚴格 lazy 的 1×1 佔位圖把輪播撐成正方形留白。
             圖用 object-contain 不裁切：規格稿滿版；「手機電腦共用同檔」模式的 1000/360
             圖完整顯示、左右透明留邊（用 cover 會上下裁 27%；容器全透明，lazy 佔位
             與載入中不再壓半透明黑底） -->
        <a
          :href="item.sUrl"
          class="hidden md:block w-full aspect-[1900/500] overflow-hidden"
        >
          <video
            v-if="item.sMediaType === 'video' && isBannerLoaded(idx)"
            :src="item.sImgUrl"
            autoplay
            muted
            loop
            playsinline
            disablepictureinpicture
            class="w-full h-full object-cover pointer-events-none md:block hidden"
            @error="onBannerImgError"
            @loadeddata="onBannerImgLoad"
          />
          <img
            v-else-if="item.sMediaType !== 'video'"
            :src="bannerSrc(item.sImgUrl, idx)"
            alt=""
            loading="lazy"
            class="w-full h-full object-contain md:block hidden"
            @error="onBannerImgError"
            @load="onBannerImgLoad"
          />
        </a>
      </SwiperSlide>
    </Swiper>
  </div>
</template>
<style>
.content-swiper-pagination {
  position: relative !important;
  bottom: 2em !important;
  transform: none !important;
}
.swiper-pagination-bullet {
  display: inline-block !important;
  border-radius: 100%;
  margin: 10px !important;
  width: 10px !important;
  height: 10px !important;
  background-color: rgb(247, 247, 247) !important;
}

/* change color of previous bullet to white*/

.swiper-pagination-bullet-active {
  border-radius: 12px !important;
  width: 24px !important;
  height: 10px !important;
  background-color: white !important;
}

@media (max-width: 768px) {
  .content-swiper-pagination {

  }
  .swiper-pagination {
    display: none !important;
    position: static !important;
    display: flex;
  }
  .swiper-pagination-bullet-active {
    /* border-radius: 12px !important; */
    width: 18px !important;
    height: 6px !important;
    /* background-color: white !important; */
  }
  .swiper-pagination-bullet {
    display: inline-block !important;
    border-radius: 3px;
    margin: 3px !important;
    width: 10px !important;
    height: 6px !important;
    background-color: rgb(247, 247, 247) !important;
  }
}
</style>
